import { Link } from "react-router-dom";
import { ArrowRight, Target, Handshake, Award, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";

const MILESTONES = [
  { year: "1993", event: "R.K. Traders established in Jaipur as a small automotive parts retailer." },
  { year: "2000", event: "Secured first authorized brand distributorship — began wholesale operations." },
  { year: "2008", event: "Expanded warehouse to 5,000+ sq ft with inventory management systems." },
  { year: "2015", event: "Crossed 10+ brand partnerships and 300+ active retail partners." },
  { year: "2020", event: "Modernised supply chain with digital ordering and faster logistics." },
  { year: "2024", event: "Now distributing 20+ brands across Rajasthan and neighbouring states." },
];

const VALUES = [
  {
    icon: Award,
    title: "Genuineness",
    desc: "Every product we distribute is 100% genuine, sourced directly from authorized manufacturers with full warranty support.",
  },
  {
    icon: Handshake,
    title: "Reliability",
    desc: "Consistent supply, timely delivery, and transparent business practices built over three decades of trust.",
  },
  {
    icon: Target,
    title: "Precision",
    desc: "Accurate order fulfilment with industry-standard cataloguing and part number verification for zero errors.",
  },
  {
    icon: Warehouse,
    title: "Scale",
    desc: "Large-format warehouse with deep inventory levels ensuring product availability when you need it.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" style={{ marginTop: "calc(36px + 96px)" }}>
      {/* Hero */}
      <section className="py-16 md:py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono text-xs tracking-[0.3em] text-neutral-500 uppercase block mb-3">
            About Us
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl uppercase tracking-tight">
            Built on Trust, Driven by Quality
          </h1>
          <p className="text-neutral-400 mt-4 max-w-2xl text-base md:text-lg">
            For over three decades, R.K. Traders has been a pillar of automotive parts distribution 
            in Rajasthan. Our reputation is built on genuine products, reliable service, and 
            lasting partnerships.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div>
              <span className="font-mono text-xs tracking-[0.3em] text-neutral-400 uppercase block mb-3">
                Our Story
              </span>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight text-black mb-6">
                From a Counter Shop to Rajasthan's Distribution Powerhouse
              </h2>
              <div className="space-y-4 text-neutral-600 leading-relaxed">
                <p>
                  R.K. Traders was founded in 1993 in the heart of Jaipur by a family with a 
                  passion for the automotive industry. What began as a modest retail counter 
                  selling spare parts to local wholesalers quickly gained a reputation for 
                  stocking genuine, hard-to-find components.
                </p>
                <p>
                  Through the early 2000s, as national brands looked for distribution partners 
                  in Rajasthan, R.K. Traders was the natural choice. Our commitment to 
                  authenticity, prompt service, and fair pricing earned us our first official 
                  distributorship — and many more followed.
                </p>
                <p>
                  Today, we operate from a modern warehouse facility on Tonk Road, Jaipur, 
                  distributing over 15 major brands to a network of 500+ retailers and 
                  workshops across Rajasthan and neighbouring states.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-xs tracking-[0.3em] text-neutral-400 uppercase block mb-3">
              Our Values
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight text-black">
              What Defines Us
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {VALUES.map((val, i) => (
              <div
                key={i}
                data-testid={`value-card-${i}`}
                className="bg-white border border-neutral-200 p-8 hover:border-black transition-colors duration-300 group"
              >
                <div className="w-12 h-12 bg-neutral-100 flex items-center justify-center mb-6 group-hover:bg-[#D32F2F] transition-colors duration-300">
                  <val.icon className="w-5 h-5 text-black group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-3">
                  {val.title}
                </h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight mb-4">
            Want to Partner With Us?
          </h2>
          <p className="text-neutral-400 max-w-lg mx-auto mb-8">
            Join our growing network of trusted retailers and workshops. 
            We offer competitive margins, reliable supply, and full brand support.
          </p>
          <Link to="/contact" data-testid="about-cta-contact">
            <Button className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] h-12 px-10 text-sm font-heading uppercase font-bold tracking-wide rounded-none cta-btn">
              Get in Touch
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
